import {
  canonicalizeTutorialProgressCommandIds,
  isTutorialProgressId,
  MAX_TUTORIAL_PROGRESS_COMPLETED_IDS,
} from "./tutorial-progress-contract.js";

const STORAGE_KEY = "fishystuff.tutorial-progress.v1";
const DOCUMENT_VERSION = 1;
const DOCUMENT_FIELDS = Object.freeze([
  "completedIds",
  "documentVersion",
  "revision",
]);
const MAX_COMPLETED_IDS = MAX_TUTORIAL_PROGRESS_COMPLETED_IDS;
const MAX_REVISION_LENGTH = 128;
const BROWSER_LOCAL_REPOSITORY_PROPERTY =
  "__fishystuffLocalTutorialProgressRepository";
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.tutorial-progress-repository.v1",
);
const BROWSER_LOCAL_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-local-tutorial-progress-repository.v1",
);

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function unavailable(operation, reason) {
  return { kind: "unavailable", operation, reason };
}

function invalid(reason) {
  return { kind: "invalid", reason };
}

function decodeStoredDocument(raw) {
  if (typeof raw !== "string") {
    return invalid("stored-value-must-be-a-string");
  }

  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    return invalid("malformed-json");
  }

  if (!isRecord(document)) {
    return invalid("document-must-be-an-object");
  }

  if (
    Number.isSafeInteger(document.documentVersion)
    && document.documentVersion > DOCUMENT_VERSION
  ) {
    return {
      kind: "unsupportedVersion",
      foundVersion: document.documentVersion,
    };
  }

  const fields = Object.keys(document).sort();
  if (
    fields.length !== DOCUMENT_FIELDS.length
    || fields.some((field, index) => field !== DOCUMENT_FIELDS[index])
  ) {
    return invalid("document-fields-must-match-v1");
  }
  if (document.documentVersion !== DOCUMENT_VERSION) {
    return invalid("document-version-must-be-1");
  }
  if (
    typeof document.revision !== "string"
    || document.revision.length === 0
    || document.revision.length > MAX_REVISION_LENGTH
  ) {
    return invalid("revision-must-be-a-non-empty-string");
  }
  if (!Array.isArray(document.completedIds)) {
    return invalid("completed-ids-must-be-an-array");
  }
  if (document.completedIds.length > MAX_COMPLETED_IDS) {
    return invalid("completed-ids-exceed-v1-limit");
  }

  let previousId = null;
  for (const tutorialId of document.completedIds) {
    if (
      !isTutorialProgressId(tutorialId)
    ) {
      return invalid(
        "completed-id-must-be-a-feature-namespaced-lowercase-token",
      );
    }
    if (previousId !== null && previousId >= tutorialId) {
      return invalid("completed-ids-must-be-sorted-and-unique");
    }
    previousId = tutorialId;
  }

  return {
    kind: "ready",
    revision: document.revision,
    completedIds: document.completedIds.slice(),
  };
}

function canonicalizeCommandIds(ids) {
  return canonicalizeTutorialProgressCommandIds(ids);
}

function expectedRevisionFrom(options) {
  if (options == null) {
    return undefined;
  }
  if (!isRecord(options)) {
    throw new TypeError("Repository command options must be an object.");
  }
  const optionFields = Object.keys(options);
  if (
    optionFields.length > 1
    || optionFields.some((field) => field !== "expectedRevision")
  ) {
    throw new TypeError("Repository command options contain unsupported fields.");
  }
  if (options.expectedRevision === undefined) {
    return undefined;
  }
  if (options.expectedRevision === null) {
    return null;
  }
  if (
    typeof options.expectedRevision !== "string"
    || options.expectedRevision.length === 0
    || options.expectedRevision.length > MAX_REVISION_LENGTH
  ) {
    throw new TypeError("Expected revision must be null or a bounded non-empty string.");
  }
  return options.expectedRevision;
}

function freezeState(value) {
  if (value.kind === "ready" || value.kind === "conflict") {
    return Object.freeze({
      ...value,
      completedIds: Object.freeze(value.completedIds.slice()),
    });
  }
  return Object.freeze({ ...value });
}

function cloneState(value) {
  return freezeState(value);
}

function statesEqual(left, right) {
  if (left.kind !== right.kind) {
    return false;
  }
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }
  for (const key of leftKeys) {
    if (key === "completedIds") {
      if (
        left.completedIds.length !== right.completedIds.length
        || left.completedIds.some((id, index) => id !== right.completedIds[index])
      ) {
        return false;
      }
    } else if (left[key] !== right[key]) {
      return false;
    }
  }
  return true;
}

function defaultCreateRevision() {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  if (cryptoRef && typeof cryptoRef.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function createTutorialProgressRepository({
  storage = null,
  eventTarget = null,
  createRevision = defaultCreateRevision,
} = {}) {
  if (typeof createRevision !== "function") {
    throw new TypeError("createRevision must be a function.");
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let disposed = false;
  const listeners = new Set();

  function state() {
    return cloneState(currentState);
  }

  function deliver(listener) {
    try {
      listener(state());
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(nextState) {
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) {
      return state();
    }
    currentState = frozen;
    if (!disposed) {
      for (const listener of listeners) {
        deliver(listener);
      }
    }
    return state();
  }

  function readAuthoritativeState() {
    let raw;
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      raw = storage.getItem(STORAGE_KEY);
    } catch (_error) {
      return unavailable("read", "storage-read-failed");
    }
    if (raw === null) {
      return { kind: "missing" };
    }
    return decodeStoredDocument(raw);
  }

  function load() {
    return publish(readAuthoritativeState());
  }

  async function exportRaw() {
    let raw;
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      raw = storage.getItem(STORAGE_KEY);
    } catch (_error) {
      return freezeState(unavailable("export", "storage-read-failed"));
    }
    if (raw === null) {
      return freezeState({ kind: "missing" });
    }
    return freezeState({
      kind: "exported",
      recordKind: decodeStoredDocument(raw).kind,
      raw,
    });
  }

  function nextRevision() {
    try {
      const revision = createRevision();
      if (
        typeof revision !== "string"
        || revision.length === 0
        || revision.length > MAX_REVISION_LENGTH
      ) {
        throw new TypeError("Revision must be a non-empty string.");
      }
      return { ok: true, revision };
    } catch (_error) {
      return { ok: false };
    }
  }

  function writeReady(completedIds) {
    const revisionResult = nextRevision();
    if (!revisionResult.ok) {
      return publish(unavailable("write", "revision-generation-failed"));
    }

    const document = {
      documentVersion: DOCUMENT_VERSION,
      revision: revisionResult.revision,
      completedIds: completedIds.slice(),
    };
    try {
      if (!storage || typeof storage.setItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      storage.setItem(STORAGE_KEY, JSON.stringify(document));
    } catch (_error) {
      return publish(unavailable("write", "storage-write-failed"));
    }
    return publish({
      kind: "ready",
      revision: document.revision,
      completedIds: document.completedIds,
    });
  }

  async function complete(ids, options = {}) {
    const commandIds = canonicalizeCommandIds(ids);
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();

    if (
      authoritative.kind === "invalid"
      || authoritative.kind === "unsupportedVersion"
      || authoritative.kind === "unavailable"
    ) {
      return publish(authoritative);
    }

    if (authoritative.kind === "missing") {
      if (expectedRevision !== undefined && expectedRevision !== null) {
        return publish({
          kind: "conflict",
          expectedRevision,
          actualRevision: null,
          completedIds: [],
        });
      }
      return writeReady(commandIds);
    }

    if (
      expectedRevision !== undefined
      && expectedRevision !== authoritative.revision
    ) {
      const effectAlreadyApplied = commandIds.every((tutorialId) =>
        authoritative.completedIds.includes(tutorialId)
      );
      if (effectAlreadyApplied) {
        return publish(authoritative);
      }
      return publish({
        kind: "conflict",
        expectedRevision,
        actualRevision: authoritative.revision,
        completedIds: authoritative.completedIds,
      });
    }

    const mergedIds = Array.from(new Set([
      ...authoritative.completedIds,
      ...commandIds,
    ])).sort();
    if (mergedIds.length > MAX_COMPLETED_IDS) {
      throw new RangeError("Tutorial progress exceeds the v1 completion limit.");
    }
    if (
      mergedIds.length === authoritative.completedIds.length
      && mergedIds.every((id, index) => id === authoritative.completedIds[index])
    ) {
      return publish(authoritative);
    }
    return writeReady(mergedIds);
  }

  async function reset(options = {}) {
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();

    if (authoritative.kind === "unavailable") {
      return publish(authoritative);
    }
    if (authoritative.kind === "missing") {
      if (expectedRevision !== undefined && expectedRevision !== null) {
        return publish({
          kind: "conflict",
          expectedRevision,
          actualRevision: null,
          completedIds: [],
        });
      }
      return publish(authoritative);
    }
    if (
      authoritative.kind === "ready"
      && expectedRevision !== undefined
      && expectedRevision !== authoritative.revision
    ) {
      return publish({
        kind: "conflict",
        expectedRevision,
        actualRevision: authoritative.revision,
        completedIds: authoritative.completedIds,
      });
    }

    try {
      if (!storage || typeof storage.removeItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      storage.removeItem(STORAGE_KEY);
    } catch (_error) {
      return publish(unavailable("remove", "storage-remove-failed"));
    }
    return publish({ kind: "missing" });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Repository listener must be a function.");
    }
    if (disposed) {
      return () => {};
    }
    listeners.add(listener);
    deliver(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function handleStorageEvent(event) {
    if (
      disposed
      || !event
      || (event.key !== STORAGE_KEY && event.key !== null)
    ) {
      return;
    }
    load();
  }

  function handlePageShow(event) {
    if (!disposed && event?.persisted === true) {
      load();
    }
  }

  if (eventTarget && typeof eventTarget.addEventListener === "function") {
    eventTarget.addEventListener("storage", handleStorageEvent);
    eventTarget.addEventListener("pageshow", handlePageShow);
  }

  function dispose() {
    if (disposed) {
      return;
    }
    disposed = true;
    listeners.clear();
    if (eventTarget && typeof eventTarget.removeEventListener === "function") {
      eventTarget.removeEventListener("storage", handleStorageEvent);
      eventTarget.removeEventListener("pageshow", handlePageShow);
    }
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: DOCUMENT_VERSION,
    exportRaw,
    load,
    state,
    complete,
    reset,
    subscribe,
    dispose,
  });
}

let browserLocalRepository = null;

function hasBrowserRepositoryMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function localBrowserRepositoryView(repository) {
  return Object.freeze({
    [BROWSER_LOCAL_REPOSITORY_BRAND]: DOCUMENT_VERSION,
    exportRaw: repository.exportRaw,
    load: repository.load,
    state: repository.state,
    complete: repository.complete,
    reset: repository.reset,
    subscribe: repository.subscribe,
  });
}

export function browserLocalTutorialProgressRepository() {
  const windowRef = globalThis.window;
  const shared = windowRef?.[BROWSER_LOCAL_REPOSITORY_PROPERTY];
  if (shared !== undefined) {
    if (
      shared[BROWSER_LOCAL_REPOSITORY_BRAND] !== DOCUMENT_VERSION
      || !hasBrowserRepositoryMethods(
        shared,
        ["exportRaw", "load", "state", "complete", "reset", "subscribe"],
      )
    ) {
      throw new TypeError("The shared local tutorial repository is incompatible.");
    }
    browserLocalRepository = shared;
  }
  if (!browserLocalRepository) {
    let storage = null;
    try {
      storage = globalThis.localStorage;
    } catch (_error) {
      storage = null;
    }
    const repository = createTutorialProgressRepository({
      storage,
      eventTarget: windowRef ?? null,
    });
    repository.load();
    browserLocalRepository = localBrowserRepositoryView(repository);
  }
  if (windowRef && typeof windowRef === "object") {
    windowRef[BROWSER_LOCAL_REPOSITORY_PROPERTY] = browserLocalRepository;
  }
  return browserLocalRepository;
}
